/**
 * Utilidades para listas de países en Select (registro, etc.).
 * Evita duplicar el país del tenant: una fila canónica al inicio y el mismo país
 * excluido del resto del array que viene del API.
 */

const norm = (s) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\u0300/g, '')

const isChileLabel = (label) => norm(label) === 'chile'

const isPeruLabel = (label) => {
  const n = norm(label)
  return n === 'peru' || n === 'perú'
}

/**
 * Una entrada por valor de campo (primer duplicado gana).
 */
export function uniqCountriesByField(list, fieldKey) {
  if (!list?.length) return []
  const seen = new Set()
  return list.filter((item) => {
    const v = item?.[fieldKey]
    if (v == null || v === '') return false
    if (seen.has(v)) return false
    seen.add(v)
    return true
  })
}

/**
 * Para catálogo ISO (`getCountriesCode`): objetos con `country`, `iso2`, etc.
 * Si el tenant es Chile o Perú: una opción canónica al inicio (datos del API si existen)
 * y el resto del listado sin filas de ese país (evita "Chile" duplicado).
 *
 * @param {Array<object>} list
 * @param {string} clientePais - p. ej. currentUser.cliente.pais ('CHI' | 'PER' | …)
 * @returns {Array<object>}
 */
export function prepareCountriesForTenantSelectIso(list, clientePais) {
  const uniq = uniqCountriesByField(list, 'country')
  return prepareTenantHeadAndRest(uniq, clientePais, 'country', {
    CHI: { label: 'Chile', iso2: 'CL' },
    PER: { label: 'Peru', iso2: 'PE' },
  })
}

/**
 * Para catálogo AdminParams (`getCountryFilter`): objetos con `name`.
 */
export function prepareCountriesForTenantSelectAdmin(list, clientePais) {
  const uniq = uniqCountriesByField(list, 'name')
  return prepareTenantHeadAndRest(uniq, clientePais, 'name', {
    CHI: { label: 'Chile', iso2: 'CL' },
    PER: { label: 'Peru', iso2: 'PE' },
  })
}

function matchesTenant(label, clientePais) {
  if (clientePais === 'CHI') return isChileLabel(label)
  if (clientePais === 'PER') return isPeruLabel(label)
  return false
}

function prepareTenantHeadAndRest(uniq, clientePais, fieldKey, canonical) {
  if (clientePais === 'CHI' || clientePais === 'PER') {
    const cfg = canonical[clientePais]
    const matchFn = clientePais === 'CHI' ? isChileLabel : isPeruLabel
    const fromApi = uniq.find((row) => matchFn(row[fieldKey]))
    const rest = uniq.filter((row) => !matchFn(row[fieldKey]))
    const head = fromApi
      ? { ...fromApi, [fieldKey]: cfg.label, ...(fromApi.iso2 ? {} : { iso2: cfg.iso2 }) }
      : { [fieldKey]: cfg.label, iso2: cfg.iso2 }
    return [head, ...rest]
  }
  return uniq
}
